import React, { SyntheticEvent, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import ProjectSearchMenu, { ProjectOption } from 'components/tasks/TasksViewer/ProjectSearchMenu';
import styles from './ParentProjectField.module.css';

interface ParentToggleProps {
  'aria-expanded'?: boolean;
  children?: React.ReactNode;
  className?: string;
  onClick?: (event: SyntheticEvent) => void;
}

const ParentToggle = React.forwardRef<HTMLButtonElement, ParentToggleProps>(({
  'aria-expanded': ariaExpanded,
  children,
  className,
  onClick,
  ...props
}, ref) => {
  const { t } = useTranslation();

  return (
    <button
      {...props}
      aria-expanded={ariaExpanded}
      aria-haspopup="menu"
      aria-label={t('parentProject')}
      className={`form-select ${styles.toggle}${className ? ` ${className}` : ''}`}
      onClick={event => {
        event.preventDefault();
        onClick?.(event);
      }}
      ref={ref}
      type="button"
    >
      {children}
    </button>
  );
});

ParentToggle.displayName = 'ParentToggle';

interface ParentProjectFieldProps {
  onChange: (parentProjectId: string | null) => void;
  projects: ProjectOption[];
  value?: string | null;
}

const ParentProjectField = ({
  onChange,
  projects,
  value,
}: ParentProjectFieldProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const selectedName = projects.find(project => project.id === value)?.name || t('noParentProject');
  const options = [
    { id: '', name: t('noParentProject') },
    ...projects,
  ];

  const selectProject = (project: ProjectOption) => {
    onChange(project.id || null);
    setIsOpen(false);
  };

  return (
    <Dropdown
      autoClose="outside"
      className={styles.field}
      onToggle={(nextShow: boolean) => setIsOpen(nextShow)}
      show={isOpen}
    >
      <Dropdown.Toggle as={ParentToggle as React.ElementType}>
        {selectedName}
      </Dropdown.Toggle>
      <Dropdown.Menu
        data-testid="parent-project-menu"
        popperConfig={{
          modifiers: [{ name: 'hide', enabled: false }],
        }}
      >
        <ProjectSearchMenu
          isOpen={isOpen}
          onSelect={selectProject}
          projects={options}
        />
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ParentProjectField;
